import { Card, CardContent } from "@/components/ui/card";
import { API_URL } from "@/lib/env";
import { getAuthHeader } from "@/utils/auth-storage";
import {
  AlertTriangle,
  CircleDashed,
  Globe,
  Lock,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { ToolViewProps } from "../../types";

type BrowserUseOutput = {
  ok?: boolean;
  success?: boolean;
  message?: string;
  url?: string;
  title?: string;
  error?: string;
  snapshot?: {
    url?: string;
    title?: string;
  };
  page?: {
    url?: string;
    finalUrl?: string;
    title?: string;
  };
  screenshot?: {
    id?: string;
    imageUrl?: string;
    contentType?: string;
    sizeBytes?: number;
  };
  image_url?: string;
  screenshotUrl?: string;
  imageUrl?: string;
  screenshot_base64?: string;
  message_id?: string;
  data?: unknown;
  actionPerformed?: string;
  elementFound?: boolean;
};

type BrowserUseInput = {
  url?: string;
  action?: string;
  instruction?: string;
};

type BrowserData = {
  url?: string;
  title?: string;
  image_url?: string;
  screenshot_base64?: string;
  message_id?: string;
};

const BROWSER_TOOL_CONFIGS = {
  browser_navigate: {
    label: "Navigate",
    description: "Navigate to a URL",
  },
  browser_act: {
    label: "Browser Action",
    description: "Execute natural language browser action",
  },
  browser_extract: {
    label: "Extract Data",
    description: "Extract data from the page",
  },
  browser_screenshot: {
    label: "Screenshot",
    description: "Capture page screenshot",
  },
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;
}

function stringField(record: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = record?.[key];
  return typeof value === "string" ? value : undefined;
}

function extractBrowserData(toolOutput: unknown): BrowserData {
  if (!toolOutput) return {};

  if (typeof toolOutput === "object") {
    const output = asRecord(toolOutput);
    const screenshot = asRecord(output?.screenshot);
    const snapshot = asRecord(output?.snapshot);
    const page = asRecord(output?.page);

    return {
      url:
        stringField(output, "url") ||
        stringField(snapshot, "url") ||
        stringField(page, "url") ||
        stringField(page, "finalUrl"),
      title:
        stringField(output, "title") ||
        stringField(snapshot, "title") ||
        stringField(page, "title"),
      image_url:
        stringField(screenshot, "imageUrl") ||
        stringField(output, "image_url") ||
        stringField(output, "screenshotUrl") ||
        stringField(output, "imageUrl"),
      screenshot_base64: stringField(output, "screenshot_base64"),
      message_id: stringField(output, "message_id"),
    };
  }

  if (typeof toolOutput === "string") {
    try {
      const parsed = JSON.parse(toolOutput);
      return extractBrowserData(parsed);
    } catch {
      const toolResultMatch = toolOutput.match(
        /ToolResult\([^)]*output='([\s\S]*?)'(?:\s*,|\s*\))/
      );
      if (toolResultMatch) {
        try {
          const outputString = toolResultMatch[1]
            .replace(/\\n/g, "\n")
            .replace(/\\"/g, '"')
            .replace(/\\u([0-9a-fA-F]{4})/g, (_, grp) =>
              String.fromCharCode(Number.parseInt(grp, 16))
            );
          const outputJson = JSON.parse(outputString);
          return extractBrowserData(outputJson);
        } catch {
          const imageUrlMatch = toolOutput.match(/"image_url":\s*"([^"]+)"/);
          const messageIdMatch = toolOutput.match(/"message_id":\s*"([^"]+)"/);
          const urlMatch = toolOutput.match(/"url":\s*"([^"]+)"/);
          const titleMatch = toolOutput.match(/"title":\s*"([^"]+)"/);

          return {
            url: urlMatch?.[1],
            title: titleMatch?.[1],
            image_url: imageUrlMatch?.[1],
            message_id: messageIdMatch?.[1],
          };
        }
      }
    }
  }

  return {};
}

/**
 * Browser window dots (traffic lights)
 */
const BrowserDots = () => (
  <div className="flex items-center gap-1.5 mr-4">
    <div className="w-3 h-3 rounded-full bg-red-400" />
    <div className="w-3 h-3 rounded-full bg-yellow-400" />
    <div className="w-3 h-3 rounded-full bg-green-400" />
  </div>
);

/**
 * Browser address bar with URL and status
 */
const BrowserAddressBar = ({
  url,
  isLoading,
  isSecure,
}: {
  url?: string;
  isLoading: boolean;
  isSecure: boolean;
}) => {
  const displayUrl = url || "about:blank";

  return (
    <div className="flex-1 min-w-0 flex items-center h-8 px-3 rounded-full bg-muted/60 border border-border/50 text-sm">
      {isLoading ? (
        <CircleDashed className="w-3.5 h-3.5 mr-2 text-muted-foreground animate-spin flex-shrink-0" />
      ) : isSecure ? (
        <Lock className="w-3.5 h-3.5 mr-2 text-muted-foreground flex-shrink-0" />
      ) : (
        <Globe className="w-3.5 h-3.5 mr-2 text-muted-foreground flex-shrink-0" />
      )}
      <span className="truncate text-muted-foreground">{displayUrl}</span>
    </div>
  );
};

/**
 * Image loader component
 */
const ImageLoader = () => (
  <div className="flex items-center justify-center w-full h-full min-h-[300px]">
    <div className="text-center">
      <CircleDashed className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
      <p className="text-sm text-muted-foreground">Loading page...</p>
    </div>
  </div>
);

function resolveApiRelativeUrl(path: string): string {
  if (/^https?:\/\//.test(path) || path.startsWith("data:")) {
    return path;
  }

  const apiOrigin = API_URL.replace(/\/api\/?$/, "");
  return `${apiOrigin}${path.startsWith("/") ? path : `/${path}`}`;
}

export const BrowserToolView: React.FC<ToolViewProps> = ({ tool }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const { actionData, isLoading, extractedData } = useMemo(() => {
    const input = tool.input as BrowserUseInput | undefined;
    const output =
      tool.state === "output-available"
        ? (tool.output as BrowserUseOutput)
        : undefined;

    const toolType = tool.type.replace("tool-", "");
    const config =
      BROWSER_TOOL_CONFIGS[toolType as keyof typeof BROWSER_TOOL_CONFIGS];
    const extracted = extractBrowserData(output);

    return {
      actionData: {
        toolType,
        input: input || {},
        output: output || {},
        config: config || {
          label: "Browser Action",
          description: "Browser automation",
        },
        success: output?.success ?? output?.ok ?? true,
      },
      isCompleted:
        tool.state === "output-available" || tool.state === "output-error",
      isLoading: !tool.state || tool.state === "running" || tool.state === "streaming",
      extractedData: extracted,
    };
  }, [tool]);

  useEffect(() => {
    const imageUrl = extractedData.image_url;
    const screenshotBase64 = extractedData.screenshot_base64;

    if (!imageUrl && !screenshotBase64) {
      setImageSrc(null);
      setImageLoading(false);
      setImageError(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    async function loadScreenshot() {
      setImageLoading(true);
      setImageError(false);

      if (screenshotBase64) {
        setImageSrc(`data:image/png;base64,${screenshotBase64}`);
        return;
      }

      if (!imageUrl) {
        setImageSrc(null);
        return;
      }

      if (!imageUrl.startsWith("/api/")) {
        setImageSrc(imageUrl);
        return;
      }

      try {
        const authHeader = getAuthHeader();
        const response = await fetch(resolveApiRelativeUrl(imageUrl), {
          headers: authHeader ? { Authorization: authHeader } : undefined,
        });

        if (!response.ok) {
          throw new Error(`Screenshot request failed with ${response.status}`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        if (!cancelled) {
          setImageSrc(objectUrl);
        }
      } catch {
        if (!cancelled) {
          setImageSrc(null);
          setImageLoading(false);
          setImageError(true);
        }
      }
    }

    loadScreenshot();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [extractedData.image_url, extractedData.screenshot_base64]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const currentUrl = extractedData.url || actionData.input.url || actionData.output.url;
  const isSecure = currentUrl?.startsWith("https://") ?? false;
  const pageTitle = extractedData.title || actionData.output.title || "New Tab";

  const renderScreenshot = () => {
    if (!imageSrc && !imageError) return null;

    return (
      <div className="relative w-full h-full">
        {imageLoading && <ImageLoader />}
        {imageSrc && (
          <img
            src={imageSrc}
            alt="Browser Screenshot"
            className={`w-full h-auto object-contain ${imageLoading ? "hidden" : "block"}`}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
        {imageError && !imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <div className="text-center text-muted-foreground">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
              <p>Failed to load screenshot</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    const hasScreenshot = extractedData.image_url || extractedData.screenshot_base64 || imageSrc || imageError;

    if (isLoading) {
      return <ImageLoader />;
    }

    if (hasScreenshot) {
      return renderScreenshot();
    }

    return (
      <div className="flex flex-col items-center justify-center w-full h-full min-h-[300px] bg-muted/30">
        <Globe className="h-16 w-16 text-muted-foreground/30 mb-4" />
        <p className="text-sm text-muted-foreground">No preview available</p>
      </div>
    );
  };

  return (
    <Card className="flex flex-col h-full overflow-hidden border-0 shadow-none rounded-none">
      {/* Browser Chrome - Tab Bar */}
      <div className="flex items-center h-10 bg-muted/40 border-b px-2">
        <BrowserDots />
        {/* Tab */}
        <div className="flex items-center h-8 max-w-[200px] px-3 bg-background rounded-t-lg border border-b-0 border-border/50">
          {isLoading ? (
            <CircleDashed className="w-3.5 h-3.5 mr-2 text-primary animate-spin flex-shrink-0" />
          ) : (
            <Globe className="w-3.5 h-3.5 mr-2 text-muted-foreground flex-shrink-0" />
          )}
          <span className="text-xs truncate">{pageTitle}</span>
        </div>
      </div>

      {/* Browser Chrome - Address Bar */}
      <div className="flex items-center h-10 bg-background border-b px-3 gap-2">
        <BrowserAddressBar url={currentUrl} isLoading={isLoading} isSecure={isSecure} />
      </div>

      {/* Browser Content */}
      <CardContent className="flex-1 p-0 overflow-auto bg-white dark:bg-zinc-900">
        {renderContent()}
      </CardContent>

      {/* Status Bar */}
      <div className="flex items-center justify-between h-8 px-3 bg-muted/30 border-t text-xs">
        {/* Left side - Status message */}
        <div className="flex items-center gap-2 text-muted-foreground">
          {isLoading ? (
            <>
              <CircleDashed className="h-3 w-3 animate-spin text-blue-500" />
              <span>Connecting to {currentUrl ? new URL(currentUrl).hostname : 'site'}...</span>
            </>
          ) : actionData.success ? (
            <>
              <div className="h-2 w-2 rounded-full bg-emerald-500" />
              <span>Page loaded successfully</span>
            </>
          ) : (
            <>
              <div className="h-2 w-2 rounded-full bg-rose-500" />
              <span>Failed to load page</span>
            </>
          )}
        </div>

        {/* Right side - Action type & security */}
        <div className="flex items-center gap-3 text-muted-foreground">
          {currentUrl?.startsWith("https://") && (
            <div className="flex items-center gap-1">
              <Lock className="h-3 w-3" />
              <span>Secure</span>
            </div>
          )}
          <span className="text-muted-foreground/60">•</span>
          <span>{actionData.config.label}</span>
        </div>
      </div>
    </Card>
  );
};
